/**
 * Generate JSON Modal Component
 * Shows legacy config-reg.json format for PR creation
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryItem, ConfigRegistryFormData } from '../types/configRegistryTypes';
import { generateLegacyConfigJson, copyToClipboard } from '../utils/generateLegacyJson';

interface GenerateJsonModalProps {
  open: boolean;
  onClose: () => void;
  config: ConfigRegistryItem | ConfigRegistryFormData;
  isFormData?: boolean;
}

export const GenerateJsonModal: React.FC<GenerateJsonModalProps> = ({
  open,
  onClose,
  config,
  isFormData = false,
}) => {
  const [copied, setCopied] = useState(false);

  const jsonString = generateLegacyConfigJson(config, isFormData);

  const handleCopy = async () => {
    const success = await copyToClipboard(jsonString);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          📋 Generate JSON for PR
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Legacy <code>config-reg.json</code> format for creating pull requests:
        </Typography>

        {/* JSON Display */}
        <Box
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflowX: 'auto',
            position: 'relative',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {jsonString}
          </pre>
        </Box>

        {/* Copy Success Alert */}
        {copied && (
          <Alert
            icon={<CheckCircleIcon fontSize="inherit" />}
            severity="success"
            sx={{ mt: 2 }}
          >
            Copied to clipboard!
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <BglSecondarySmallButton onClick={onClose}>
          Close
        </BglSecondarySmallButton>
        <BglPrimarySmallButton
          onClick={handleCopy}
          startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

